import { ElementProps } from '../../../../../@types/forms/types';
import { CalendarEventType } from '../../../../../@types/calendar/types';
import { ElementValidationsProps } from '../../../../../@types/forms/constants/ElementValidations';
import { ElementTextStyleProps, ElementTextDecorationProps } from '../utils/elementSettingsUtils';

export type HyperlinkProps = {
  text: string;
  url: string;
};

export type CountryProps = {
  code: string;
  label: string;
};

export type FormGroupOptionProps = {
  label: string;
  isChecked: boolean;
};

export type CustomListProps = {
  id: number;
  name: string;
  options?: string[];
};

export type ChildElement = {
  id: string;
  name: string;
};

/**
 * Consolidated state for element settings
 */
export type ElementSettingsState = {
  selectedElement: ElementProps | null;

  // Text styling
  elementTextStyle: ElementTextStyleProps | null;
  elementTextDecoration: ElementTextDecorationProps | null;
  textRotation: number | string | Array<number | string> | null;
  selectedColor: string | null;

  // Options
  options: string[];
  formGroupOptions: FormGroupOptionProps[];

  // Custom list
  customLists: CustomListProps[];
  selectedList: CustomListProps | null;

  // Image
  uploadedFile: string | ArrayBuffer | null;

  // Static text
  staticText: string | undefined;

  // Date settings
  daysOfCountry: CountryProps;
  dateRangeTemplate: string | undefined;
  calendarEventType: CalendarEventType | null;
  todayDate: boolean;
  isPastDisabled: boolean;

  // UI state
  isOpenTextRotation: boolean;
  isOpenColor: boolean;
  openedTabIndex: number;

  // Validation
  isElementRequired: boolean;
  validation: ElementValidationsProps;

  // Hyperlink
  hyperlink: HyperlinkProps;

  // User data
  userInfo: number | null | undefined;

  // Registration number
  prefix: string | undefined;

  // Number of characters
  numberOfCharacters: number[];

  // Child elements
  selectedChildElement: ChildElement | null;
  childElements: ChildElement[];
  sameTypeElementsList: ChildElement[];

  // Address book
  isForExternal: boolean;
  isForInternal: boolean;
  isForAll: boolean;
};

/**
 * Actions for element settings reducer
 */
export enum ElementSettingsActionType {
  SET_SELECTED_ELEMENT = 'SET_SELECTED_ELEMENT',
  UPDATE_ELEMENT_FIELD = 'UPDATE_ELEMENT_FIELD',
  SET_TEXT_STYLE = 'SET_TEXT_STYLE',
  SET_TEXT_DECORATION = 'SET_TEXT_DECORATION',
  SET_TEXT_ROTATION = 'SET_TEXT_ROTATION',
  SET_COLOR = 'SET_COLOR',
  SET_OPTIONS = 'SET_OPTIONS',
  ADD_OPTION = 'ADD_OPTION',
  DELETE_OPTION = 'DELETE_OPTION',
  UPDATE_OPTION = 'UPDATE_OPTION',
  SET_FORM_GROUP_OPTIONS = 'SET_FORM_GROUP_OPTIONS',
  ADD_FORM_GROUP_OPTION = 'ADD_FORM_GROUP_OPTION',
  DELETE_FORM_GROUP_OPTION = 'DELETE_FORM_GROUP_OPTION',
  UPDATE_FORM_GROUP_OPTION = 'UPDATE_FORM_GROUP_OPTION',
  SET_CUSTOM_LISTS = 'SET_CUSTOM_LISTS',
  SET_SELECTED_LIST = 'SET_SELECTED_LIST',
  SET_UPLOADED_FILE = 'SET_UPLOADED_FILE',
  SET_STATIC_TEXT = 'SET_STATIC_TEXT',
  SET_DAYS_OF_COUNTRY = 'SET_DAYS_OF_COUNTRY',
  SET_DATE_RANGE_TEMPLATE = 'SET_DATE_RANGE_TEMPLATE',
  SET_CALENDAR_EVENT_TYPE = 'SET_CALENDAR_EVENT_TYPE',
  SET_TODAY_DATE = 'SET_TODAY_DATE',
  SET_PAST_DISABLED = 'SET_PAST_DISABLED',
  TOGGLE_TEXT_ROTATION = 'TOGGLE_TEXT_ROTATION',
  TOGGLE_COLOR = 'TOGGLE_COLOR',
  SET_TAB_INDEX = 'SET_TAB_INDEX',
  SET_ELEMENT_REQUIRED = 'SET_ELEMENT_REQUIRED',
  SET_VALIDATION = 'SET_VALIDATION',
  SET_HYPERLINK = 'SET_HYPERLINK',
  SET_USER_INFO = 'SET_USER_INFO',
  SET_PREFIX = 'SET_PREFIX',
  SET_NUMBER_OF_CHARACTERS = 'SET_NUMBER_OF_CHARACTERS',
  SET_SELECTED_CHILD_ELEMENT = 'SET_SELECTED_CHILD_ELEMENT',
  SET_CHILD_ELEMENTS = 'SET_CHILD_ELEMENTS',
  ADD_CHILD_ELEMENT = 'ADD_CHILD_ELEMENT',
  DELETE_CHILD_ELEMENT = 'DELETE_CHILD_ELEMENT',
  SET_SAME_TYPE_ELEMENTS_LIST = 'SET_SAME_TYPE_ELEMENTS_LIST',
  SET_ADDRESS_BOOK_TYPE = 'SET_ADDRESS_BOOK_TYPE',
  RESET_STATE = 'RESET_STATE',
  INITIALIZE_FROM_ELEMENT = 'INITIALIZE_FROM_ELEMENT',
}

export type ElementSettingsAction =
  | { type: ElementSettingsActionType.SET_SELECTED_ELEMENT; payload: ElementProps | null }
  | { type: ElementSettingsActionType.UPDATE_ELEMENT_FIELD; payload: Partial<ElementProps> }
  | { type: ElementSettingsActionType.SET_TEXT_STYLE; payload: ElementTextStyleProps | null }
  | {
      type: ElementSettingsActionType.SET_TEXT_DECORATION;
      payload: ElementTextDecorationProps | null;
    }
  | {
      type: ElementSettingsActionType.SET_TEXT_ROTATION;
      payload: number | string | Array<number | string> | null;
    }
  | { type: ElementSettingsActionType.SET_COLOR; payload: string | null }
  | { type: ElementSettingsActionType.SET_OPTIONS; payload: string[] }
  | { type: ElementSettingsActionType.ADD_OPTION; payload: string }
  | { type: ElementSettingsActionType.DELETE_OPTION; payload: number }
  | { type: ElementSettingsActionType.UPDATE_OPTION; payload: { index: number; value: string } }
  | { type: ElementSettingsActionType.SET_FORM_GROUP_OPTIONS; payload: FormGroupOptionProps[] }
  | { type: ElementSettingsActionType.ADD_FORM_GROUP_OPTION; payload: FormGroupOptionProps }
  | { type: ElementSettingsActionType.DELETE_FORM_GROUP_OPTION; payload: number }
  | {
      type: ElementSettingsActionType.UPDATE_FORM_GROUP_OPTION;
      payload: { index: number; value: FormGroupOptionProps };
    }
  | { type: ElementSettingsActionType.SET_CUSTOM_LISTS; payload: CustomListProps[] }
  | { type: ElementSettingsActionType.SET_SELECTED_LIST; payload: CustomListProps | null }
  | { type: ElementSettingsActionType.SET_UPLOADED_FILE; payload: string | ArrayBuffer | null }
  | { type: ElementSettingsActionType.SET_STATIC_TEXT; payload: string | undefined }
  | { type: ElementSettingsActionType.SET_DAYS_OF_COUNTRY; payload: CountryProps }
  | { type: ElementSettingsActionType.SET_DATE_RANGE_TEMPLATE; payload: string | undefined }
  | {
      type: ElementSettingsActionType.SET_CALENDAR_EVENT_TYPE;
      payload: CalendarEventType | null;
    }
  | { type: ElementSettingsActionType.SET_TODAY_DATE; payload: boolean }
  | { type: ElementSettingsActionType.SET_PAST_DISABLED; payload: boolean }
  | { type: ElementSettingsActionType.TOGGLE_TEXT_ROTATION }
  | { type: ElementSettingsActionType.TOGGLE_COLOR }
  | { type: ElementSettingsActionType.SET_TAB_INDEX; payload: number }
  | { type: ElementSettingsActionType.SET_ELEMENT_REQUIRED; payload: boolean }
  | { type: ElementSettingsActionType.SET_VALIDATION; payload: ElementValidationsProps }
  | { type: ElementSettingsActionType.SET_HYPERLINK; payload: HyperlinkProps }
  | { type: ElementSettingsActionType.SET_USER_INFO; payload: number | null | undefined }
  | { type: ElementSettingsActionType.SET_PREFIX; payload: string | undefined }
  | { type: ElementSettingsActionType.SET_NUMBER_OF_CHARACTERS; payload: number[] }
  | { type: ElementSettingsActionType.SET_SELECTED_CHILD_ELEMENT; payload: ChildElement | null }
  | { type: ElementSettingsActionType.SET_CHILD_ELEMENTS; payload: ChildElement[] }
  | { type: ElementSettingsActionType.ADD_CHILD_ELEMENT; payload: ChildElement }
  | { type: ElementSettingsActionType.DELETE_CHILD_ELEMENT; payload: number }
  | { type: ElementSettingsActionType.SET_SAME_TYPE_ELEMENTS_LIST; payload: ChildElement[] }
  | {
      type: ElementSettingsActionType.SET_ADDRESS_BOOK_TYPE;
      payload: { isForAll: boolean; isForExternal: boolean; isForInternal: boolean };
    }
  | { type: ElementSettingsActionType.RESET_STATE; payload: string }
  | { type: ElementSettingsActionType.INITIALIZE_FROM_ELEMENT; payload: ElementProps };
